import type { BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'
import { action, thunk, type Action, type Thunk } from 'easy-peasy'
import type { FMPStoreModel } from '../../../types'
// import type { CohesionGlobal } from '../../../../types/window'
import type { CohesionGlobal } from '@strata-monorepo/shared-types'
import { hasError } from '../../../utils/validateFileUploads'
import { getStrataApiBaseUrl } from '@strata-monorepo/utils/getStrataApiBaseUrl'

export interface UploadEnergyBillPayload {
  files: FileList | File[]
  cohesionData: CohesionGlobal
}

export interface UploadedBillsModel {
  // States
  uploadedData: BillEntryData[]

  // Actions
  setUploadedData: Action<UploadedBillsModel, BillEntryData[]>
  addUploadedData: Action<UploadedBillsModel, BillEntryData | BillEntryData[]>
  removeUploadedByIndex: Action<UploadedBillsModel, number>

  // Thunks
  uploadEnergyBill: Thunk<
    UploadedBillsModel,
    UploadEnergyBillPayload,
    null,
    FMPStoreModel,
    Promise<BillEntryData[]>
  >
}

export const uploadedBillsModel = (): UploadedBillsModel => ({
  // States
  uploadedData: [],

  // Actions
  setUploadedData: action((state, payload) => {
    state.uploadedData = payload
  }),

  addUploadedData: action((state, payload) => {
    if (Array.isArray(payload)) {
      state.uploadedData.unshift(...payload)
    } else {
      state.uploadedData.unshift(payload)
    }
  }),

  removeUploadedByIndex: action((state, index) => {
    state.uploadedData = state.uploadedData.filter((_, i) => i !== index)
  }),

  // Thunks
  uploadEnergyBill: thunk(
    async (actions, { files, cohesionData }, { getState }): Promise<BillEntryData[]> => {
      const fileArray = Array.from(files)

      const dataToUpload: BillEntryData[] = []

      // Start all per-file tasks concurrently
      const tasks = fileArray.map(async (file) => {
        const fileHasError = await hasError(file)
        if (fileHasError) {
          dataToUpload.push({
            error: fileHasError,
            usage: null,
            month: '',
            day: null,
            year: null,
            address: '',
            billing: null,
            source: 'ocr',
            planName: '',
            fileName: file.name,
            isSubmitted: true,
            isEditing: false,
          })
          return
        }

        const abortController = new AbortController()
        let timeoutId: ReturnType<typeof setTimeout> | undefined

        // Set timeout for upload request based on env var or default to 20 seconds
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => {
              abortController.abort()
              reject(new Error('Upload timed out. Please try again.'))
            },
            Number(import.meta.env.VITE_BILL_UPLOAD_TIMEOUT) || 20000,
          )
        })

        // Race the timeout promise against the upload promise
        try {
          const result = await Promise.race([
            uploadEnergyBillAPI(file, cohesionData?.sessionId),
            timeoutPromise,
          ])

          dataToUpload.push({
            error: undefined,
            usage: result.usage,
            month: result.month,
            day: result.day,
            year: result.year,
            address: result.address,
            billing: result.billing,
            planName: result.planName,
            fileName: file.name,
            source: 'ocr',
            isSubmitted: true,
            isEditing: false,
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed'

          dataToUpload.push({
            error: message,
            usage: null,
            month: '',
            day: null,
            year: null,
            address: '',
            billing: null,
            planName: '',
            fileName: file.name,
            source: 'ocr',
            isSubmitted: true,
            isEditing: false,
          })
        } finally {
          if (timeoutId) clearTimeout(timeoutId)
        }
      })

      // Wait for all tasks to finish
      await Promise.all(tasks)
      actions.addUploadedData(dataToUpload)

      const { uploadedData: latestUploadedData } = getState()
      return latestUploadedData
    },
  ),
})

const uploadEnergyBillAPI = async (file: File, sessionId: string): Promise<BillEntryData> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    const baseUrl = getStrataApiBaseUrl()
    const url = `${baseUrl}/api/v1/ds/bill-upload`
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'session-id': sessionId,
      },
    })
    if (!res.ok) {
      throw new Error(`Request failed with status ${res.status}`)
    }

    const response = await res.json()
    return response.data
  } catch (error) {
    throw error
  }
}
